import React, { useState, useEffect, useRef } from "react";
import {
    Table,
    Input,
    Button,
    Space,
    Spin, Tooltip
} from "antd";
import { message } from "../../components/antdMessageBridge";
import { SearchOutlined } from "@ant-design/icons";
import { FileTextOutlined } from '@ant-design/icons';
import CommonObj from "../../CommonObj";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import "./clientFeedbackService.css";
import { exportToExcel } from '../exporttoexcel/recycleexporttoexcel';
import { runGridExport } from "../../components/gridExportHelper";
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";
import { Link, useLocation, useNavigationType } from "react-router-dom";
import { renderCommonPaginationTotal } from "../../components/commonpagination/common-pagination";
import "../../components/commonpagination/common-pagination.css";
import ConfirmationModal from "../../components/confirmationmodal/confirmation-modal";

const DeliverToS3Feedback = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const { accessToken } = useAuthToken();

    const searchInput = useRef(null);
    const location = useLocation();
    const navType = useNavigationType();
    const isBrowserBack = navType === "POP";
    const returningFromChild = location.state?.fromChild && !isBrowserBack;
    const [searchFilters, setsearchFilters] = useState(returningFromChild ? location.state?.searchFilters : {});
    const [sortedInfo, setSortedInfo] = useState(returningFromChild ? location.state?.sortedInfo : {});
    const [pagination, setPagination] = useState(returningFromChild ? location.state?.pagination : {
        current: 1,
        pageSize: 10,
        total: 0
    });
    const fromChild = true;
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    const handleReset = (dataIndex, clearFilters) => {
        clearFilters();
        setsearchFilters((prev) => {
            const updated = { ...prev };
            delete updated[dataIndex];
            return updated;
        });
        fetchData({
            pagination,
            sorter: sortedInfo,
            filters: { ...searchFilters, [dataIndex]: undefined },
        });
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    style={{ marginBottom: 8, display: "block" }}
                    onPressEnter={() => confirm()}
                />
                <Space>
                    <Button type="primary" size="small" onClick={() => confirm()}>Search</Button>
                    <Button size="small" onClick={() => {
                        handleReset(dataIndex, clearFilters);
                        confirm();
                    }}>Reset</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />),
        filteredValue: searchFilters[dataIndex] ? [searchFilters[dataIndex]] : null,
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        sortOrder: sortedInfo.columnKey === dataIndex ? sortedInfo.order : null
    });

    const rerunThisTask = async (record) => {
        if (!record.taskId || !record.executionId) {
            message.warning("Missing Task ID or Execution ID for this record.");
            return;
        }

        const payload = {
            taskId: record.taskId,
            executionId: record.executionId
        };
        setLoading(true);

        try {
            const myHeaders = {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
            };

            const response = await fetch(CommonObj.Endpoints.InsertDeliveryS3Failure, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.detail || result.error || "Network response was not ok");
            }

            message.success(result.Message || "Task re-run successfully!");
            console.log("Newly Inserted ID:", result.Id);

            fetchData({ pagination, sorter: sortedInfo, filters: searchFilters });

        } catch (err) {
            console.error("Error re-running task:", err);
            message.error(err.message || "An unexpected error occurred while re-running the task.");
        } finally {
            setLoading(false);
        }
    };
    const columns = [
        {
            title: "File Name",
            dataIndex: "fileName",
            key: "fileName",
            width: 300,
            ...getColumnSearchProps("fileName"),
            render: (text, record) => (
                <Link
                    to="/processed-delivery-feedback"
                    state={{
                        dsid: record.dsId ?? record.dsid,
                        parentState: {
                            searchFilters,
                            sortedInfo,
                            pagination,
                            fromChild,
                        },
                    }}
                    style={{
                        color: '#1890ff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        textDecoration: 'underline'
                    }}
                >
                    <FileTextOutlined />
                    {text}
                </Link>
            ),
        },
        {
            title: "Task Id",
            dataIndex: "taskId",
            key: "taskId",
            width: 240,
            ...getColumnSearchProps("taskId"),
        },
        {
            title: "Execution Id",
            dataIndex: "executionId",
            key: "executionId",
            width: 240,
            ...getColumnSearchProps("executionId"),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 130,
        },
        {
            title: "Start Time",
            dataIndex: "startTime",
            key: "startTime",
            width: 180,
        },
        {
            title: "End Time",
            dataIndex: "endTime",
            key: "endTime",
            width: 180,
        },
        {
            title: "Total Bids",
            dataIndex: "totalBids",
            key: "totalBids",
            width: 130,
            render: (value) => value ?? "-",
        },
        {
            title: "Accepted Records",
            dataIndex: "acceptedRecords",
            key: "acceptedRecords",
            width: 160,
            render: (value) => value ?? "-",
        },
        {
            title: "Rejected Records",
            dataIndex: "rejectedRecords",
            key: "rejectedRecords",
            width: 160,
            render: (value) => value ?? "-",
        }
    ];

    if (isSuperAdmin) {
        columns.push({
            title: "Action",
            key: "actions",
            width: 100,
            align: "center",
            render: (_, record) => {
                const isDisabled = record.hoursForDisable !== "0";
                const tooltipMessage = isDisabled
                    ? `You can try after ${record.hoursForDisable}`
                    : "Re-process";

                return (
                    <div>
                        <Space size="middle">
                            <Tooltip title={tooltipMessage}>
                                <span style={{ cursor: isDisabled ? "not-allowed" : "pointer", display: "inline-block" }}>
                                    <button
                                        type="button"
                                        disabled={isDisabled}
                                        data-track="deliveryToS3-automation-re-process-action"
                                        style={{
                                            cursor: isDisabled ? "not-allowed" : "pointer",
                                            opacity: isDisabled ? 0.4 : 1,
                                            border: "none",
                                            background: "transparent",
                                            padding: 0
                                        }}
                                        onClick={() => {
                                            if (isDisabled) return;

                                            ConfirmationModal.show({
                                                title: "Confirm Re-process",
                                                content: "Are you sure you want to re-process this task?",
                                                okText: "Yes",
                                                cancelText: "No",
                                                onOk: () => rerunThisTask(record),
                                            });
                                        }}
                                    >
                                        <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g clip-path="url(#clip0_5244_39974)">
                                                <path d="M5.53317 8.72222H4.93317V8.72222L5.53317 8.72222ZM5.53317 9.41667L5.08399 9.81445C5.19222 9.93667 5.34557 10.0095 5.50868 10.0162C5.6718 10.0228 5.83058 9.96274 5.94842 9.84976L5.53317 9.41667ZM7.16524 8.6831C7.40444 8.45376 7.41243 8.07395 7.1831 7.83476C6.95376 7.59556 6.57395 7.58757 6.33476 7.8169L6.75 8.25L7.16524 8.6831ZM4.94918 7.85221C4.72949 7.60414 4.35029 7.58113 4.10221 7.80082C3.85414 8.02051 3.83113 8.39971 4.05082 8.64779L4.5 8.25L4.94918 7.85221ZM11.1712 6.78244C11.3963 7.02569 11.7759 7.04047 12.0191 6.81544C12.2624 6.59042 12.2771 6.21081 12.0521 5.96756L11.6117 6.375L11.1712 6.78244ZM9.03278 5.25V4.65C6.77308 4.65 4.93317 6.46876 4.93317 8.72222H5.53317H6.13317C6.13317 7.14037 7.42692 5.85 9.03278 5.85V5.25ZM5.53317 8.72222L4.93317 8.72222L4.93317 9.41667H5.53317H6.13317L6.13317 8.72222L5.53317 8.72222ZM5.53317 9.41667L5.94842 9.84976L7.16524 8.6831L6.75 8.25L6.33476 7.8169L5.11793 8.98357L5.53317 9.41667ZM5.53317 9.41667L5.98236 9.01888L4.94918 7.85221L4.5 8.25L4.05082 8.64779L5.08399 9.81445L5.53317 9.41667ZM11.6117 6.375L12.0521 5.96756C11.3031 5.15782 10.2267 4.65 9.03278 4.65V5.25V5.85C9.88006 5.85 10.6409 6.20913 11.1712 6.78244L11.6117 6.375Z" fill="#002449" />
                                                <path d="M12.4641 8.58301L12.9126 8.18451C12.8043 8.06252 12.6509 7.98994 12.4878 7.98348C12.3248 7.97702 12.1662 8.03724 12.0485 8.15026L12.4641 8.58301ZM10.8344 9.31628C10.5954 9.54581 10.5877 9.92564 10.8173 10.1646C11.0468 10.4036 11.4266 10.4113 11.6656 10.1818L11.25 9.74902L10.8344 9.31628ZM13.0515 10.1475C13.2715 10.3953 13.6508 10.4177 13.8985 10.1976C14.1462 9.97749 14.1686 9.59825 13.9485 9.35052L13.5 9.74902L13.0515 10.1475ZM6.8125 11.2276C6.58574 10.986 6.20603 10.974 5.9644 11.2007C5.72278 11.4275 5.71073 11.8072 5.9375 12.0488L6.375 11.6382L6.8125 11.2276ZM8.9511 12.7497V13.3497C11.216 13.3497 13.0641 11.5331 13.0641 9.27745H12.4641H11.8641C11.8641 10.8572 10.5665 12.1497 8.9511 12.1497V12.7497ZM12.4641 9.27745H13.0641V8.58301H12.4641H11.8641V9.27745H12.4641ZM12.4641 8.58301L12.0485 8.15026L10.8344 9.31628L11.25 9.74902L11.6656 10.1818L12.8797 9.01575L12.4641 8.58301ZM12.4641 8.58301L12.0155 8.98151L13.0515 10.1475L13.5 9.74902L13.9485 9.35052L12.9126 8.18451L12.4641 8.58301ZM6.375 11.6382L5.9375 12.0488C6.68857 12.8491 7.76191 13.3497 8.9511 13.3497V12.7497V12.1497C8.10468 12.1497 7.3446 11.7946 6.8125 11.2276L6.375 11.6382Z" fill="#002449" />
                                                <circle opacity="0.5" cx="9" cy="9" r="7.5" stroke="#002449" stroke-width="1.2" />
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_5244_39974">
                                                    <rect width="18" height="18" fill="white" />
                                                </clipPath>
                                            </defs>
                                        </svg>
                                    </button>
                                </span>
                            </Tooltip>
                        </Space>
                    </div>
                );
            },
        });
    }
    const getRowClassName = (record, index) => {
        return index % 2 === 0 ? "odd-row" : "even-row";
    };

    const handleTableChange = (newPagination, filters = {}, sorter = {}) => {
        const normalizeFilters = {};
        Object.keys(filters).forEach((key) => {
            if (filters[key] && filters[key].length > 0) {
                normalizeFilters[key] = filters[key][0];
            }
        });
        const cleanSorter = Array.isArray(sorter) ? sorter[0] : sorter; // Handle multi-sort if applicable
        const serializableSorter = {
            field: cleanSorter.field,
            columnKey: cleanSorter.columnKey,
            order: cleanSorter.order,
        };

        setsearchFilters(normalizeFilters);
        setSortedInfo(serializableSorter);
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));

        fetchData({
            pagination: { current: newPagination.current, pageSize: newPagination.pageSize },
            sorter,
            filters: normalizeFilters,
        });
    };

    const handleExport = async () => {
        const exportSearch = Object.keys(searchFilters || {}).length
            ? JSON.stringify(searchFilters)
            : "";
        const payload = {
            pageNo: 1,
            pageSize: 0,
            search: exportSearch,
            orderByColumn: "CreatedOn",
            orderBy: "DESC",
        };
        const myHeaders = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            "X-CSRF-Token": encryptTimestamp()
        };
        await runGridExport({
            request: fetch(CommonObj.Endpoints.GetDeliveryToS3AutomationLog, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            }),
            noDataMarkers: ["No books found."],
            exportFn: exportToExcel,
            columns,
            filename: 'Delivery To S3 Automation Log',
        });
    };

    const fetchData = async ({ pagination: pag, filters, sorter }) => {
        const requestSearch = Object.keys(filters || {}).length
            ? JSON.stringify(filters)
            : "";
        const payload = {
            pageNo: pag?.current,
            pageSize: pag?.pageSize || 10,
            search: requestSearch,
            orderByColumn: sorter?.field || "CreatedOn",
            orderBy: sorter?.order === "ascend" ? "ASC" : "DESC",
        };
        setLoading(true);
        try {
            const myHeaders = {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
            };
            const response = await fetch(CommonObj.Endpoints.GetDeliveryToS3AutomationLog, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            const responseText = await response.text();
            let result;

            try {
                result = JSON.parse(responseText);
            } catch (e) {
                if (responseText.includes("No logs found")) {
                    result = [];
                } else {
                    throw new Error(responseText || 'Failed to parse API response.');
                }
            }
            const books = Array.isArray(result) ? result : [result];
            const formattedData = books.map((item, index) => ({
                ...item,
                key: `${item.executionId || item.taskId || item.dsId}-${index}`,
            }));

            setData(formattedData);
            const total = formattedData[0]?.totalRows || 0;
            setPagination((prev) => ({
                ...prev,
                current: pag?.current || prev.current,
                pageSize: pag?.pageSize || prev.pageSize,
                total,
            }));
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const storedRoles = localStorage.getItem('userRoles');
        try {
            if (storedRoles) {
                const parsedRoles = JSON.parse(storedRoles);

                if (parsedRoles.length > 0 && parsedRoles[0].roleName) {
                    const rolesArray = parsedRoles[0].roleName.split(',').map(role => role.trim());
                    setIsSuperAdmin(rolesArray.includes('Super Admin'));
                }
            }
        } catch (error) {
            console.error("Failed to parse user roles from local storage:", error);
        }
        fetchData({ pagination, sorter: sortedInfo, filters: searchFilters });
    }, []);

    if (loading) {
        return (
            <Spin size="large" style={{ margin: "20px auto" }} />
        );
    }

    return (
        <div className="active-page-container">
            <div className="active-page-header-container">
                <div className="active-page-header-title-container">
                    <h4>Delivery to S3 automation Queue</h4>
                </div>
            </div>
            <div className="active-page-body-container">
                <div className="rounded-table-wrapper">
                    <Table
                        columns={columns}
                        dataSource={data}
                        className="common-pagination common-grid"
                        pagination={{
                            ...pagination,
                            pageSizeOptions: ["10", "20", "30", "40", "50", "100"],
                            showSizeChanger: true,
                            position: ["bottomRight"],
                            defaultPageSize: 10,
                            showTotal: (total, range) =>
                                renderCommonPaginationTotal(total, range, {
                                    pagination,
                                    handleTableChange,
                                    searchFilters,
                                    sortedInfo
                                }),
                        }}
                        rowKey={(record) => record.key}
                        scroll={{ x: "max-content", y: "calc(100vh - 280px)" }}
                        rowClassName={getRowClassName}
                        onChange={handleTableChange}
                    />
                </div>
            </div>
        </div>
    );
};

export default DeliverToS3Feedback;
